/**
 * Menu contestuale (tasto destro) leggero, senza dipendenze aggiuntive.
 *
 * Il pacchetto @floraltech/ui non espone un ContextMenu e non ha fra le
 * dipendenze @radix-ui/react-context-menu: invece di aggiungere un pacchetto
 * per un solo menu, qui c'e' un componente autonomo di poche righe che copre
 * quel che serve (apertura su tasto destro alla posizione del cursore,
 * chiusura con Escape, click fuori, scroll o resize).
 *
 * Lo stile ricalca DropdownMenuContent di ui_core cosi' il menu non stona con
 * gli altri menu dell'applicazione.
 */

import React, { useEffect, useRef } from 'react';

export interface VoceMenu {
  /** Testo mostrato. */
  etichetta: string;
  /** Icona lucide opzionale. */
  icona?: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  /** Voce distruttiva o di allerta: testo rosso. */
  pericolosa?: boolean;
}

interface Props {
  /** Posizione del cursore alla pressione del tasto destro. */
  posizione: { x: number; y: number };
  voci: VoceMenu[];
  onChiudi: () => void;
}

export function MenuContestuale({ posizione, voci, onChiudi }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const chiudiSuTasto = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onChiudi();
    };
    const chiudiSuClickFuori = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onChiudi();
    };
    document.addEventListener('keydown', chiudiSuTasto);
    document.addEventListener('mousedown', chiudiSuClickFuori);
    // Scroll e resize spostano il riferimento sotto il menu: meglio chiuderlo
    // che lasciarlo ancorato a una posizione che non significa piu' nulla.
    window.addEventListener('scroll', onChiudi, true);
    window.addEventListener('resize', onChiudi);
    return () => {
      document.removeEventListener('keydown', chiudiSuTasto);
      document.removeEventListener('mousedown', chiudiSuClickFuori);
      window.removeEventListener('scroll', onChiudi, true);
      window.removeEventListener('resize', onChiudi);
    };
  }, [onChiudi]);

  // Il menu non deve uscire dalla finestra: se non ci sta lo si ribalta.
  const larghezza = 240;
  const altezzaStimata = voci.length * 36 + 8;
  const x = Math.min(posizione.x, window.innerWidth - larghezza - 8);
  const y = Math.min(posizione.y, window.innerHeight - altezzaStimata - 8);

  if (voci.length === 0) return null;

  return (
    <div
      ref={ref}
      role="menu"
      style={{ top: Math.max(8, y), left: Math.max(8, x), width: larghezza }}
      className="fixed z-50 overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
    >
      {voci.map((voce) => {
        const Icona = voce.icona;
        return (
          <button
            key={voce.etichetta}
            type="button"
            role="menuitem"
            onClick={() => {
              onChiudi();
              voce.onClick();
            }}
            className={`flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground ${
              voce.pericolosa ? 'text-destructive' : ''
            }`}
          >
            {Icona ? <Icona className="h-4 w-4 shrink-0" /> : null}
            {voce.etichetta}
          </button>
        );
      })}
    </div>
  );
}
